import React from 'react';
import PageWithHeader from "../common/PageWithHeader";
import { Grid, Card, CardContent, Typography, Paper, Button, Box } from '@mui/material';
import {Link, useResolvedPath} from 'react-router-dom';
import { useAppSelector } from "../../redux/hooks";
import { styled } from '@mui/material/styles';
import {useRouter} from "../../hooks/useRouter";

const StyledCard = styled(Card)(() => ({
  height: '230px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#1976d2',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  transition: '0.3s',
  borderRadius: '15px',
  '&:hover': {
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
  },
}));

const ComingSoonCard = styled(Card)(() => ({
  height: '230px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#b0b0b0',
  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  borderRadius: '15px',
}));

const StyledTypography = styled(Typography)({
  textAlign: 'center',
  color: '#fff',
});

const EmployeeReviewDashboard: React.FC = () => {
  const router = useRouter();
  const url = useResolvedPath("").pathname.replace(/\/$/, '');

  const currentUser = useAppSelector(state => state.auth.user);
  const isAdmin = currentUser?.roles && currentUser.roles.includes('admin');
  const canAccessReviewTools = currentUser?.roles?.includes('user') === true;
  
  // Check if user has company-wide view (CEO, COO, admin)
  const hasCompanyWideView = currentUser?.roles?.includes('admin') || 
    currentUser?.roles?.includes('ceo') || 
    currentUser?.roles?.includes('coo');
  
  // Check if user is c-suite (can view their team's dashboard)
  const isCSuite = currentUser?.roles?.includes('c-suite');
  
  // Determine dashboard button label
  const dashboardButtonLabel = hasCompanyWideView ? 'CEO Dashboard' : isCSuite ? 'My Team Dashboard' : 'My Dashboard';

  const options: Array<{ id: number; title: string; link: string; comingSoon?: boolean }> = [
    { id: 1, title: 'Life Wheel', link: `${url}/life-wheel` },
    { id: 2, title: 'SPEAR', link: `${url}/spear` },
    { id: 3, title: 'BSC', link: `${url}/bsc` },
    { id: 4, title: 'PEAR', link: `${url}/pear` },
    { id: 6, title: '360 Degree Survey', link: `${url}/360-degree-survey` },
    ...(canAccessReviewTools ? [{ id: 7, title: 'Casting Tool', link: `${url}/castingtool` }] : []),
  ];

  return (
    <PageWithHeader>
      <Paper elevation={2} sx={{ margin: 10, padding: 5, borderRadius: '15px' }}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant='h1' gutterBottom sx={{ fontWeight: 'bold', color: '#123456' }}>
              Review Dashboard
            </Typography>
          </Grid>
          {currentUser?.roles &&
            <Grid item xs={6} container justifyContent="flex-end" alignItems="center" gap={2}>
              <Button sx={{ display: 'inline-block' }} color="secondary" onClick={() => router.push('/reviews/ceo-dashboard')}>
                <Typography color="secondary" align="right">{dashboardButtonLabel}</Typography>
              </Button>
              {isAdmin && (
                <Button sx={{ display: 'inline-block' }} color="primary" onClick={() => router.push('/reviews/admin')}>
                  <Typography color="primary" align="right">Admin Dashboard</Typography>
                </Button>
              )}
            </Grid>
          }
        </Grid>
        <Typography variant='body1' gutterBottom>
          Welcome to the Employee Review Dashboard. This platform allows you to submit your self-assessment reports,
          track your performance, and view feedback from your peers and supervisors. Below, you will find various tools
          and reports to help you in your self-assessment journey:
        </Typography>
        <Box sx={{ typography: 'body1' }}>
          <ul>
            <li><strong>Life Wheel:</strong> A monthly self-assessment tool that helps you evaluate different areas of your life.</li>
            <li><strong>SPEAR:</strong> A monthly performance evaluation framework focusing on specific, measurable goals.</li>
            <li><strong>BSC (Balance Score Card):</strong> A quarterly review tool that aligns your activities with the organization’s vision and strategy.</li>
            <li><strong>PEAR:</strong> A two-part quarterly review focusing on your professional and personal growth.</li>
            <li><strong>360 Degree Survey:</strong> A comprehensive feedback tool that gathers input from your peers, subordinates, and supervisors.</li>
            <li><strong>Casting Tool:</strong> A 4x4 scorecard that helps frame performance and team development in simple quadrant language.</li>
          </ul>
        </Box>
        <Grid container spacing={3} columns={12}>
          {options.map((option) => (
            <Grid item xs={12} sm={6} md={4} key={option.id}>
              {option.comingSoon ? (
                <ComingSoonCard>
                  <CardContent>
                    <StyledTypography variant="h6">
                      {option.title}
                      <Typography variant="caption" display="block" sx={{ marginTop: 1 }}>
                        COMING SOON
                      </Typography>
                    </StyledTypography>
                  </CardContent>
                </ComingSoonCard>
              ) : (
                <Link to={option.link} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                  <StyledCard>
                    <CardContent>
                      <StyledTypography variant="h6">
                        {option.title}
                      </StyledTypography>
                    </CardContent>
                  </StyledCard>
                </Link>
              )}
            </Grid>
          ))}
        </Grid>
      </Paper>
    </PageWithHeader>
  );
}

export default EmployeeReviewDashboard;
